// Pure geometry helpers for TemplateFieldEditor snapping.
//
// All coordinates are PDF points in the editor's storage space: bottom-left
// origin, where a field's `y` is the text baseline (pdf-lib drawText draws the
// baseline at exactly `y`, so snapping a field's y to a template text
// baseline is pixel-perfect in the generated PDF — not an approximation).

export type Rect = { x: number; y: number; width: number; height: number };

export type SnapSource = "field" | "content";

export type SnapTarget = { v: number; source: SnapSource };

/** Sorted ascending by `v`. */
export type AxisTargets = SnapTarget[];

export type PageSnapTargets = {
  /** Candidates for the field's left OR right edge (other fields' edges, template text edges). */
  edgeX: AxisTargets;
  /** Candidates for the field's left edge only (4pt right of "Label:" template runs). */
  leftX: AxisTargets;
  /** Candidates for the field's horizontal center (other fields' centers). */
  centerX: AxisTargets;
  /** Candidates for the field's baseline / bottom edge. */
  baselineY: AxisTargets;
  /** Candidates for the field's top edge. */
  topY: AxisTargets;
  /** Candidates for the field's vertical center. */
  centerY: AxisTargets;
};

export type ContentSnapTargets = {
  baselinesY: number[];
  edgesX: number[];
  labelRightX: number[];
  hasTextLayer: boolean;
};

export type Guide = { axis: "v" | "h"; at: number; source: SnapSource };

export type SnapResult = { x: number; y: number; guides: Guide[] };

/** Content targets outrank field targets at equal distance. */
const CONTENT_WEIGHT = 0.75;

/** Gap between a template label's ":" and where its value naturally starts. */
const LABEL_VALUE_GAP = 4;

export const EMPTY_CONTENT: ContentSnapTargets = {
  baselinesY: [],
  edgesX: [],
  labelRightX: [],
  hasTextLayer: false,
};

export function roundHalf(v: number): number {
  return Math.round(v * 2) / 2;
}

/** Round a field to 0.5pt so stored JSONB never carries float noise. */
export function quantize<T extends Rect & { fontSize?: number }>(f: T): T {
  return {
    ...f,
    x: roundHalf(f.x),
    y: roundHalf(f.y),
    width: roundHalf(f.width),
    height: roundHalf(f.height),
    ...(f.fontSize != null ? { fontSize: roundHalf(f.fontSize) } : {}),
  };
}

/**
 * Assign each value to a cluster (greedy sweep over sorted values, breaking
 * where the gap exceeds `tol`) and return each value's cluster mean, in the
 * original order.
 */
export function clusterAssign(values: number[], tol: number): number[] {
  if (values.length === 0) return [];
  const order = values.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
  const out = new Array<number>(values.length);
  let start = 0;
  const flush = (end: number) => {
    let sum = 0;
    for (let k = start; k < end; k++) sum += order[k].v;
    const mean = sum / (end - start);
    for (let k = start; k < end; k++) out[order[k].i] = mean;
  };
  for (let k = 1; k < order.length; k++) {
    if (order[k].v - order[k - 1].v > tol) {
      flush(k);
      start = k;
    }
  }
  flush(order.length);
  return out;
}

/** Deduplicate values within `tol`, returning sorted cluster means. */
export function cluster(values: number[], tol: number): number[] {
  const assigned = clusterAssign(values, tol);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const v of assigned) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out.sort((a, b) => a - b);
}

/** The subset of pdf.js `getTextContent()` items we consume. */
export type TextItemLike = { str: string; transform: number[]; width: number };

/**
 * Extract snap targets from a page's text content. `toEditor` maps a PDF
 * user-space point to editor storage space — identity for the common case
 * (rotation 0, CropBox origin at 0,0); the editor passes a viewport-based
 * transform otherwise.
 */
export function buildContentSnapTargets(
  items: TextItemLike[],
  toEditor: (x: number, y: number) => { x: number; y: number } = (x, y) => ({
    x,
    y,
  })
): ContentSnapTargets {
  const baselines: number[] = [];
  const edges: number[] = [];
  const labels: number[] = [];
  for (const it of items) {
    if (!it || typeof it.str !== "string" || it.str.trim() === "") continue;
    const t = it.transform;
    if (!t || t.length < 6) continue;
    // Rotated/sheared runs have no horizontal baseline to snap to.
    if (Math.abs(t[1]) > 0.01 || Math.abs(t[2]) > 0.01) continue;
    if (!(it.width > 0)) continue;
    const start = toEditor(t[4], t[5]);
    const end = toEditor(t[4] + it.width, t[5]);
    baselines.push(start.y);
    edges.push(start.x, end.x);
    if (it.str.trimEnd().endsWith(":")) labels.push(end.x + LABEL_VALUE_GAP);
  }
  const baselinesY = cluster(baselines, 0.5);
  return {
    baselinesY,
    edgesX: cluster(edges, 0.5),
    labelRightX: cluster(labels, 0.5),
    hasTextLayer: baselinesY.length > 0,
  };
}

/** Merge other placed fields + template content into per-axis sorted targets. */
export function buildPageSnapTargets(
  others: Rect[],
  content: ContentSnapTargets = EMPTY_CONTENT
): PageSnapTargets {
  const edgeX: SnapTarget[] = [];
  const leftX: SnapTarget[] = [];
  const centerX: SnapTarget[] = [];
  const baselineY: SnapTarget[] = [];
  const topY: SnapTarget[] = [];
  const centerY: SnapTarget[] = [];
  for (const o of others) {
    edgeX.push(
      { v: o.x, source: "field" },
      { v: o.x + o.width, source: "field" }
    );
    centerX.push({ v: o.x + o.width / 2, source: "field" });
    baselineY.push({ v: o.y, source: "field" });
    topY.push({ v: o.y + o.height, source: "field" });
    centerY.push({ v: o.y + o.height / 2, source: "field" });
  }
  for (const v of content.edgesX) edgeX.push({ v, source: "content" });
  for (const v of content.labelRightX) leftX.push({ v, source: "content" });
  for (const v of content.baselinesY) baselineY.push({ v, source: "content" });
  const byV = (a: SnapTarget, b: SnapTarget) => a.v - b.v;
  return {
    edgeX: edgeX.sort(byV),
    leftX: leftX.sort(byV),
    centerX: centerX.sort(byV),
    baselineY: baselineY.sort(byV),
    topY: topY.sort(byV),
    centerY: centerY.sort(byV),
  };
}

/** Index of the first element with v >= value. */
function lowerBound(list: AxisTargets, value: number): number {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (list[mid].v < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

type Candidate = {
  delta: number;
  at: number;
  source: SnapSource;
  score: number;
};

/** Collect targets within `threshold` of `pos`, scored for ranking. */
function collect(
  list: AxisTargets,
  pos: number,
  threshold: number,
  out: Candidate[]
): void {
  const from = lowerBound(list, pos - threshold);
  for (let i = from; i < list.length && list[i].v <= pos + threshold; i++) {
    const t = list[i];
    const delta = t.v - pos;
    out.push({
      delta,
      at: t.v,
      source: t.source,
      score: Math.abs(delta) * (t.source === "content" ? CONTENT_WEIGHT : 1),
    });
  }
}

function best(cands: Candidate[]): Candidate | null {
  let b: Candidate | null = null;
  for (const c of cands) if (!b || c.score < b.score) b = c;
  return b;
}

/**
 * Snap a candidate rect against the page targets. X and Y snap independently;
 * `threshold` is in PDF points (caller passes SNAP_PX / zoomScale so the feel
 * is constant in screen pixels).
 */
export function computeSnap(
  rect: Rect,
  targets: PageSnapTargets,
  threshold: number
): SnapResult {
  const guides: Guide[] = [];

  const xCands: Candidate[] = [];
  collect(targets.edgeX, rect.x, threshold, xCands);
  collect(targets.leftX, rect.x, threshold, xCands);
  // Snapping the right edge moves the left edge by the same delta.
  const rightCands: Candidate[] = [];
  collect(targets.edgeX, rect.x + rect.width, threshold, rightCands);
  xCands.push(...rightCands);
  const centerXCands: Candidate[] = [];
  collect(targets.centerX, rect.x + rect.width / 2, threshold, centerXCands);
  xCands.push(...centerXCands);
  const bx = best(xCands);

  const yCands: Candidate[] = [];
  collect(targets.baselineY, rect.y, threshold, yCands);
  const topCands: Candidate[] = [];
  collect(targets.topY, rect.y + rect.height, threshold, topCands);
  yCands.push(...topCands);
  const centerYCands: Candidate[] = [];
  collect(targets.centerY, rect.y + rect.height / 2, threshold, centerYCands);
  yCands.push(...centerYCands);
  const by = best(yCands);

  const x = bx ? rect.x + bx.delta : rect.x;
  const y = by ? rect.y + by.delta : rect.y;
  if (bx) guides.push({ axis: "v", at: bx.at, source: bx.source });
  if (by) guides.push({ axis: "h", at: by.at, source: by.source });
  return { x, y, guides };
}

// ── Alignment tools (operate on copies; caller re-quantizes on commit) ──

export type AlignEdge = "left" | "right" | "top" | "baseline";

export function alignRects<T extends Rect>(fields: T[], edge: AlignEdge): T[] {
  if (fields.length < 2) return fields;
  switch (edge) {
    case "left": {
      const v = Math.min(...fields.map((f) => f.x));
      return fields.map((f) => ({ ...f, x: v }));
    }
    case "right": {
      const v = Math.max(...fields.map((f) => f.x + f.width));
      return fields.map((f) => ({ ...f, x: v - f.width }));
    }
    case "top": {
      const v = Math.max(...fields.map((f) => f.y + f.height));
      return fields.map((f) => ({ ...f, y: v - f.height }));
    }
    case "baseline": {
      // Align to the topmost baseline (least surprising: fields rise to it).
      const v = Math.max(...fields.map((f) => f.y));
      return fields.map((f) => ({ ...f, y: v }));
    }
  }
}

/** Equalize baseline-to-baseline gaps between the highest and lowest field. */
export function distributeVertically<T extends Rect>(fields: T[]): T[] {
  if (fields.length < 3) return fields;
  const order = fields.map((f, i) => ({ f, i })).sort((a, b) => b.f.y - a.f.y); // top of page first (larger y = higher)
  const top = order[0].f.y;
  const bottom = order[order.length - 1].f.y;
  const step = (top - bottom) / (order.length - 1);
  const out = fields.slice();
  order.forEach(({ i }, k) => {
    out[i] = { ...out[i], y: top - step * k };
  });
  return out;
}

/**
 * "Tidy up": cluster near-equal left edges and baselines across the given
 * fields (within `tol` pt) and unify each cluster to its mean.
 */
export function tidyRects<T extends Rect>(fields: T[], tol = 3): T[] {
  if (fields.length < 2) return fields;
  const xs = clusterAssign(
    fields.map((f) => f.x),
    tol
  );
  const ys = clusterAssign(
    fields.map((f) => f.y),
    tol
  );
  return fields.map((f, i) => ({ ...f, x: xs[i], y: ys[i] }));
}
